import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import {
  Checkbox,
  Combobox,
  Select,
  TextInput,
  useCombobox,
} from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import AvatarGroup, {
  Avatar,
} from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  ASSIGN_INCOMING_VOE_EMPLOYEES,
  ASSIGN_OUTGOING_VOE_EMPLOYEES,
  GET_COMPANY,
  UPDATE_COMPANY,
} from 'src/graphql/companies.graphql'
import { formatVerificationPeriod } from 'src/lib/voeVerificationPeriod'
import { EmployeeStatus, RoleType } from 'src/graphql/types/employees'

import FieldLabel from './FieldLabel'

interface Employee {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  status?: EmployeeStatus
  roleType?: RoleType
}

interface VerificationRequestsCardProps {
  companyData: {
    companyId: string
    incomingVoEAssignedEmployees?: Employee[] | null
    outgoingVoEAssignedEmployees?: Employee[] | null
    voeVerificationPeriodMonths?: number | null
    autoCancelOutOfWindowVoEs?: boolean | null
  }
  recruiters?: Employee[]
  initialLoading?: boolean
  editing: boolean
}

export interface VerificationRequestsCardRef {
  save: () => Promise<void>
  cancel: () => void
  loading: boolean
  canEdit: boolean
}

const VerificationRequestsCard = forwardRef<
  VerificationRequestsCardRef,
  VerificationRequestsCardProps
>(({
  companyData,
  recruiters = [],
  initialLoading = false,
  editing,
}, ref) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [incomingEmployeeIds, setIncomingEmployeeIds] = useState<string[]>([])
  const [outgoingEmployeeIds, setOutgoingEmployeeIds] = useState<string[]>([])
  const [verificationPeriodMonths, setVerificationPeriodMonths] = useState<
    number | null
  >(companyData.voeVerificationPeriodMonths ?? 36)
  const [autoCancelOutOfWindow, setAutoCancelOutOfWindow] = useState(
    companyData.autoCancelOutOfWindowVoEs ?? false
  )
  const incomingCombobox = useCombobox()
  const outgoingCombobox = useCombobox()

  const [assignIncomingVoEEmployees, { loading: loadingIncoming }] =
    useMutation(ASSIGN_INCOMING_VOE_EMPLOYEES, {
      onCompleted: () => {
        toast('Incoming verification employees updated successfully', 'success')
      },
      onError: (error) => {
        toast(
          `Failed to update incoming verification employees: ${error.message}`,
          'error'
        )
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { id: companyData.companyId },
        },
      ],
      awaitRefetchQueries: true,
    })

  const [assignOutgoingVoEEmployees, { loading: loadingOutgoing }] =
    useMutation(ASSIGN_OUTGOING_VOE_EMPLOYEES, {
      onCompleted: () => {
        toast('Outgoing verification employees updated successfully', 'success')
      },
      onError: (error) => {
        toast(
          `Failed to update outgoing verification employees: ${error.message}`,
          'error'
        )
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { id: companyData.companyId },
        },
      ],
      awaitRefetchQueries: true,
    })

  const [updateCompany, { loading: loadingUpdateCompany }] = useMutation(
    UPDATE_COMPANY,
    {
      onCompleted: () => {
        toast('Verification period updated successfully', 'success')
      },
      onError: (error) => {
        toast(`Failed to update verification period: ${error.message}`, 'error')
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { id: companyData.companyId },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      const incomingIds = Array.isArray(
        companyData.incomingVoEAssignedEmployees
      )
        ? companyData.incomingVoEAssignedEmployees
            .map((emp) => emp?.employeeId)
            .filter((id): id is string => Boolean(id))
        : []
      setIncomingEmployeeIds(incomingIds)

      const outgoingIds = Array.isArray(
        companyData.outgoingVoEAssignedEmployees
      )
        ? companyData.outgoingVoEAssignedEmployees
            .map((emp) => emp?.employeeId)
            .filter((id): id is string => Boolean(id))
        : []
      setOutgoingEmployeeIds(outgoingIds)

      setVerificationPeriodMonths(
        companyData.voeVerificationPeriodMonths ?? 36
      )
      setAutoCancelOutOfWindow(
        companyData.autoCancelOutOfWindowVoEs ?? false
      )
    }
  }, [companyData, editing])

  const getEmployeeLabel = (employee: Employee | null | undefined): string => {
    if (!employee) {
      return ''
    }

    const firstName = employee.firstName?.trim() ?? ''
    const lastName = employee.lastName?.trim() ?? ''
    const fullName = [firstName, lastName].filter(Boolean).join(' ')

    if (fullName) {
      return fullName
    }

    if (employee.email) {
      return employee.email
    }

    return employee.employeeId ?? ''
  }

  const recruiterOptions = useMemo(() => {
    const normalizedRecruiters = Array.isArray(recruiters) ? recruiters : []

    const activeRecruiters = normalizedRecruiters
      .filter(
        (employee) =>
          employee?.status === EmployeeStatus.ACTIVE &&
          employee?.roleType === RoleType.RECRUITER &&
          employee?.employeeId
      )
      .map((employee) => ({
        value: employee.employeeId,
        label: getEmployeeLabel(employee),
        employee,
      }))

    const assignedEmployees = [
      ...(Array.isArray(companyData?.incomingVoEAssignedEmployees)
        ? companyData.incomingVoEAssignedEmployees
        : []),
      ...(Array.isArray(companyData?.outgoingVoEAssignedEmployees)
        ? companyData.outgoingVoEAssignedEmployees
        : []),
    ]
      .filter((employee) => employee?.employeeId)
      .map((employee) => ({
        value: employee.employeeId as string,
        label: getEmployeeLabel(employee),
        employee,
      }))

    const combined = [...activeRecruiters]

    assignedEmployees.forEach((assigned) => {
      if (!combined.some((option) => option.value === assigned.value)) {
        combined.push(assigned)
      }
    })

    return combined.sort((a, b) => a.label.localeCompare(b.label))
  }, [
    recruiters,
    companyData?.incomingVoEAssignedEmployees,
    companyData?.outgoingVoEAssignedEmployees,
  ])

  const filteredIncomingOptions = useMemo(() => {
    const search = incomingCombobox.searchValue?.trim().toLowerCase() ?? ''

    if (!search) {
      return recruiterOptions
    }

    return recruiterOptions.filter((option) =>
      option.label.toLowerCase().includes(search)
    )
  }, [incomingCombobox.searchValue, recruiterOptions])

  const filteredOutgoingOptions = useMemo(() => {
    const search = outgoingCombobox.searchValue?.trim().toLowerCase() ?? ''

    if (!search) {
      return recruiterOptions
    }

    return recruiterOptions.filter((option) =>
      option.label.toLowerCase().includes(search)
    )
  }, [outgoingCombobox.searchValue, recruiterOptions])

  const incomingAvatars: Avatar[] = useMemo(() => {
    if (!companyData.incomingVoEAssignedEmployees) {
      return []
    }

    return companyData.incomingVoEAssignedEmployees
      .filter((emp) => emp?.employeeId)
      .map((emp) => ({
        firstName: emp.firstName ?? '',
        lastName: emp.lastName ?? '',
        isInactive: emp.status ? emp.status !== EmployeeStatus.ACTIVE : false,
      }))
  }, [companyData.incomingVoEAssignedEmployees])

  const outgoingAvatars: Avatar[] = useMemo(() => {
    if (!companyData.outgoingVoEAssignedEmployees) {
      return []
    }

    return companyData.outgoingVoEAssignedEmployees
      .filter((emp) => emp?.employeeId)
      .map((emp) => ({
        firstName: emp.firstName ?? '',
        lastName: emp.lastName ?? '',
        isInactive: emp.status ? emp.status !== EmployeeStatus.ACTIVE : false,
      }))
  }, [companyData.outgoingVoEAssignedEmployees])

  const handleCancel = () => {
    // Reset to original values unconditionally
    const incomingIds = Array.isArray(companyData.incomingVoEAssignedEmployees)
      ? companyData.incomingVoEAssignedEmployees
          .map((emp) => emp?.employeeId)
          .filter((id): id is string => Boolean(id))
      : []
    setIncomingEmployeeIds(incomingIds)

    const outgoingIds = Array.isArray(companyData.outgoingVoEAssignedEmployees)
      ? companyData.outgoingVoEAssignedEmployees
          .map((emp) => emp?.employeeId)
          .filter((id): id is string => Boolean(id))
      : []
    setOutgoingEmployeeIds(outgoingIds)
    setVerificationPeriodMonths(
      companyData.voeVerificationPeriodMonths ?? 36
    )
    setAutoCancelOutOfWindow(
      companyData.autoCancelOutOfWindowVoEs ?? false
    )
  }

  const handleSave = async () => {
    if (!companyData.companyId) return

    const mutations: Promise<any>[] = [
      assignIncomingVoEEmployees({
        variables: {
          companyId: companyData.companyId,
          employeeIds: incomingEmployeeIds,
        },
      }),
      assignOutgoingVoEEmployees({
        variables: {
          companyId: companyData.companyId,
          employeeIds: outgoingEmployeeIds,
        },
      }),
    ]

    const periodChanged =
      verificationPeriodMonths !==
      (companyData.voeVerificationPeriodMonths ?? 36)
    const autoCancelChanged =
      autoCancelOutOfWindow !==
      (companyData.autoCancelOutOfWindowVoEs ?? false)

    if (periodChanged || autoCancelChanged) {
      mutations.push(
        updateCompany({
          variables: {
            id: companyData.companyId,
            input: {
              voeVerificationPeriodMonths: verificationPeriodMonths,
              autoCancelOutOfWindowVoEs: autoCancelOutOfWindow,
            },
          },
        })
      )
    }

    await Promise.all(mutations)
  }

  const handleIncomingToggle = (employeeId: string) => {
    if (incomingEmployeeIds.includes(employeeId)) {
      setIncomingEmployeeIds(
        incomingEmployeeIds.filter((id) => id !== employeeId)
      )
    } else {
      setIncomingEmployeeIds([...incomingEmployeeIds, employeeId])
    }
    incomingCombobox.openDropdown()
  }

  const handleOutgoingToggle = (employeeId: string) => {
    if (outgoingEmployeeIds.includes(employeeId)) {
      setOutgoingEmployeeIds(
        outgoingEmployeeIds.filter((id) => id !== employeeId)
      )
    } else {
      setOutgoingEmployeeIds([...outgoingEmployeeIds, employeeId])
    }
    outgoingCombobox.openDropdown()
  }

  const incomingSelectedNames = useMemo(() => {
    return incomingEmployeeIds
      .map((id) => recruiterOptions.find((opt) => opt.value === id)?.label)
      .filter(Boolean)
      .join(', ')
  }, [incomingEmployeeIds, recruiterOptions])

  const outgoingSelectedNames = useMemo(() => {
    return outgoingEmployeeIds
      .map((id) => recruiterOptions.find((opt) => opt.value === id)?.label)
      .filter(Boolean)
      .join(', ')
  }, [outgoingEmployeeIds, recruiterOptions])

  const loading = loadingIncoming || loadingOutgoing || loadingUpdateCompany

  useImperativeHandle(ref, () => ({
    save: handleSave,
    cancel: handleCancel,
    loading,
    canEdit,
  }))

  const verificationPeriodLabel = (
    <FieldLabel
      label="Outgoing Verification Period"
      tooltip={
        <>
          How far back to verify employment history for outgoing requests.
          Employment entries older than this period will appear as expired
          (strikethrough) in the outgoing verifications table.
          <br />
          <br />
          Default value: 36 months (3 years)
        </>
      }
    />
  )

  const autoCancelExpiredLabel = (
    <FieldLabel
      label="Auto-cancel Expired Verifications"
      tooltip={
        <>
          When enabled, outgoing verification requests for employment that ended
          before the verification period cutoff will be automatically canceled at
          creation time.
        </>
      }
    />
  )

  const incomingVoELabel = (
    <FieldLabel
      label="Incoming Verification Requests"
      tooltip={
        <>
          Employees assigned to handle incoming employment verification
          requests.
          <br />
          <br />
          Requests will be distributed among these employees using round-robin
          assignment, and email notifications will be sent to the assigned
          employee when a new request is received.
        </>
      }
    />
  )

  const outgoingVoELabel = (
    <FieldLabel
      label="Outgoing Verification Requests"
      tooltip={
        <>
          Employees assigned to handle outgoing employment verification
          requests.
          <br />
          <br />
          Requests will be distributed among these employees using round-robin
          assignment, and email notifications will be sent to the assigned
          employee when a new request is created.
        </>
      }
    />
  )

  return (
    <div className="grid grid-cols-12 gap-3">
            {/* Incoming Verification Requests */}
            <div className="col-span-3">
              {editing ? (
                <Combobox
                  store={incomingCombobox}
                  withinPortal
                  onDropdownClose={() => {
                    incomingCombobox.resetSelectedOption()
                    incomingCombobox.updateSearch('')
                  }}
                  onOptionSubmit={(value) => handleIncomingToggle(value)}
                >
                  <Combobox.Target>
                    <TextInput
                      readOnly
                      label={incomingVoELabel}
                      value={incomingSelectedNames}
                      placeholder={
                        initialLoading
                          ? 'Loading employees…'
                          : 'Select employees'
                      }
                      className="w-full"
                      disabled={initialLoading}
                      onClick={() => incomingCombobox.toggleDropdown()}
                      onFocus={() => incomingCombobox.openDropdown()}
                      rightSection={<Combobox.Chevron />}
                      rightSectionPointerEvents="none"
                      classNames={{
                        label: 'text-sm font-medium text-doubleNickel-gray-700',
                        input: 'cursor-pointer text-sm',
                      }}
                    />
                  </Combobox.Target>
                  <Combobox.Dropdown>
                    <Combobox.Search placeholder="Search employees" />
                    <Combobox.Options>
                      {initialLoading ? (
                        <Combobox.Empty>Loading employees…</Combobox.Empty>
                      ) : filteredIncomingOptions.length === 0 ? (
                        <Combobox.Empty>No employees found</Combobox.Empty>
                      ) : (
                        filteredIncomingOptions.map((option) => (
                          <Combobox.Option
                            value={option.value}
                            key={option.value}
                          >
                            <div className="pointer-events-none">
                              <Checkbox
                                checked={incomingEmployeeIds.includes(
                                  option.value
                                )}
                                label={option.label}
                                readOnly
                              />
                            </div>
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </Combobox.Dropdown>
                </Combobox>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium text-doubleNickel-gray-700">
                    {incomingVoELabel}
                  </div>
                  {incomingAvatars.length > 0 ? (
                    <AvatarGroup avatars={incomingAvatars} />
                  ) : (
                    <div className="text-sm text-doubleNickel-gray-500">
                      No employees assigned
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Outgoing Verification Requests */}
            <div className="col-span-3">
              {editing ? (
                <Combobox
                  store={outgoingCombobox}
                  withinPortal
                  onDropdownClose={() => {
                    outgoingCombobox.resetSelectedOption()
                    outgoingCombobox.updateSearch('')
                  }}
                  onOptionSubmit={(value) => handleOutgoingToggle(value)}
                >
                  <Combobox.Target>
                    <TextInput
                      readOnly
                      label={outgoingVoELabel}
                      value={outgoingSelectedNames}
                      placeholder={
                        initialLoading
                          ? 'Loading employees…'
                          : 'Select employees'
                      }
                      className="w-full"
                      disabled={initialLoading}
                      onClick={() => outgoingCombobox.toggleDropdown()}
                      onFocus={() => outgoingCombobox.openDropdown()}
                      rightSection={<Combobox.Chevron />}
                      rightSectionPointerEvents="none"
                      classNames={{
                        label: 'text-sm font-medium text-doubleNickel-gray-700',
                        input: 'cursor-pointer text-sm',
                      }}
                    />
                  </Combobox.Target>
                  <Combobox.Dropdown>
                    <Combobox.Search placeholder="Search employees" />
                    <Combobox.Options>
                      {initialLoading ? (
                        <Combobox.Empty>Loading employees…</Combobox.Empty>
                      ) : filteredOutgoingOptions.length === 0 ? (
                        <Combobox.Empty>No employees found</Combobox.Empty>
                      ) : (
                        filteredOutgoingOptions.map((option) => (
                          <Combobox.Option
                            value={option.value}
                            key={option.value}
                          >
                            <div className="pointer-events-none">
                              <Checkbox
                                checked={outgoingEmployeeIds.includes(
                                  option.value
                                )}
                                label={option.label}
                                readOnly
                              />
                            </div>
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </Combobox.Dropdown>
                </Combobox>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium text-doubleNickel-gray-700">
                    {outgoingVoELabel}
                  </div>
                  {outgoingAvatars.length > 0 ? (
                    <AvatarGroup avatars={outgoingAvatars} />
                  ) : (
                    <div className="text-sm text-doubleNickel-gray-500">
                      No employees assigned
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Outgoing Verification Period */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={verificationPeriodLabel}
                  value={verificationPeriodMonths ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setVerificationPeriodMonths(null)
                    } else {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value) && value >= 1) {
                        setVerificationPeriodMonths(value)
                      }
                    }
                  }}
                  placeholder="months"
                  className="w-full"
                  type="number"
                  min={1}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={verificationPeriodLabel}
                  value={
                    verificationPeriodMonths
                      ? formatVerificationPeriod(verificationPeriodMonths)
                      : '-'
                  }
                />
              )}
            </div>

            {/* Auto-cancel Expired Verifications */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={autoCancelExpiredLabel}
                  data={[
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' },
                  ]}
                  value={autoCancelOutOfWindow ? 'enabled' : 'disabled'}
                  onChange={(val) =>
                    setAutoCancelOutOfWindow(val === 'enabled')
                  }
                  className="w-full"
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                    input: 'text-sm',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={autoCancelExpiredLabel}
                  value={autoCancelOutOfWindow ? 'Enabled' : 'Disabled'}
                />
              )}
            </div>
          </div>
  )
})

export default VerificationRequestsCard
